import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { capabilityChain, developmentCycle } from '../../data/semanticKnowledge';
import { fadeUp } from '../../utils/motion';
export function SkpCapabilityFoundation() {
  return <section className="bg-white py-20 lg:py-28" data-id="element-1891" data-mp="1j6iziegpt4zq">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1892" data-mp="1e8r6fh4c3cty">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-1893" data-mp="1s9k1q42gmby4">
          <motion.div {...fadeUp} className="lg:col-span-6" data-id="element-1894" data-mp="1s7h3wxv2feiu">
            <h2 className="font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight mb-6" data-id="element-1895" data-mp="1n9pecahw84ht">
              The knowledge foundation for CJI and NCJI
            </h2>
            <p className="text-xl text-ink-dark/80 leading-relaxed mb-6" data-id="element-1896" data-mp="1x1bnm1ao7thx">
              The Human Capability Platform uses SKP to maintain the
              definitions and evidence behind the{' '}
              <a href="#cji" className="text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy" data-id="element-1897" data-mp="26zhl86pg2dgz">
                Civic Judgment Index (CJI)
              </a>{' '}
              and{' '}
              <a href="#ncji" className="text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy" data-id="element-1898" data-mp="1df86k6j9dtlx">
                Neurocognitive Civic Judgment Index (NCJI)
              </a>
              .
            </p>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1899" data-mp="1sc8qhnv7kk9u">
              SKP connects each capability domain to its indicators, assessment
              tasks, interpretation rules, learning activities, and later
              outcomes. It records which versions were used so that an
              assessment can be explained and, when necessary, reconsidered.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-6" data-id="element-1900" data-mp="2b308fdt9hizu">
            <div className="bg-surface-muted rounded-lg p-8" data-id="element-1901" data-mp="2b0dqck8qvl3d">
              <div className="flex items-center justify-between mb-6" data-id="element-1902" data-mp="2g2mkhjxcst72">
                <p className="text-sm font-medium text-ink-dark" data-id="element-1903" data-mp="1qcwb6z0q5xwh">Connected in SKP</p>
                <span className="text-xs font-medium text-navy bg-white border border-line rounded px-2 py-1" data-id="element-1904" data-mp="1lecq10tygq56">
                  Version recorded
                </span>
              </div>
              <ol className="space-y-2" data-id="element-1905" data-mp="20523k9uw2hc7">
                {capabilityChain.map((item, index) => <li key={item} className="flex items-center gap-4 bg-white border border-line rounded-md px-4 py-3" data-id="element-1906" data-mp="1vao15nht79y1" data-mpi={index}>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${index === 0 ? 'bg-navy' : 'bg-teal'}`} data-id="element-1907" data-mp="244koh5mpmsf1" />
                    <span className={`text-ink-dark ${index === 0 ? 'font-heading font-bold' : 'font-medium'}`} data-id="element-1908" data-mp="2e2qmvd9b5gf5">
                      {item}
                    </span>
                  </li>)}
              </ol>
            </div>
          </motion.div>
        </div>

        <motion.div {...fadeUp} className="mt-20 pt-14 border-t border-line" data-id="element-1909" data-mp="2vdngamc1jtvn">
          <p className="text-lg text-ink-muted leading-relaxed max-w-3xl mb-10" data-id="element-1910" data-mp="30bp1pdwxw5aa">
            This supports the NCJI’s developmental purpose: identifying an area
            for growth, explaining the finding, recommending a pathway, and
            measuring progress over time.
          </p>
          <ol className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0" data-id="element-1911" data-mp="2clbeq1t7owmk">
            {developmentCycle.map((step, index) => <li key={step.title} className="flex items-start md:pr-6" data-id="element-1912" data-mp="2go7wxreuaqpz" data-mpi={index}>
                <div className="flex-1" data-id="element-1913" data-mp="22ogm1zjmdqg7">
                  <p className="font-heading font-bold text-2xl text-navy mb-1" data-id="element-1914" data-mp="22phv5yh2s2va">
                    {step.title}
                  </p>
                  <p className="text-ink-muted" data-id="element-1915" data-mp="wjzluvwyw6n">{step.body}</p>
                </div>
                {index < developmentCycle.length - 1 && <ArrowRight size={20} aria-hidden="true" className="hidden md:block text-line mt-1.5 shrink-0" data-id="element-1916" data-mp="5u26zgua93cj" />}
              </li>)}
          </ol>
          <p className="mt-10 text-lg text-ink-muted leading-relaxed max-w-3xl" data-id="element-1917" data-mp="1sxlboat8hnq4">
            Opportunity and role models can be connected to demonstrated
            capabilities while keeping role requirements separate from a
            person’s assessment record.
          </p>
          <p className="mt-10 font-heading font-bold text-2xl lg:text-3xl text-ink-dark leading-snug max-w-4xl border-l-4 border-teal pl-6" data-id="element-1918" data-mp="1j1i76yawtd00">
            A recommendation is a starting point for review and development,
            not an irreversible verdict about a person.
          </p>
        </motion.div>
      </div>
    </section>;
}